import { Client } from 'app/models/client.models';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ClientesService } from 'app/services/clientes.service';
import { MatDialog } from '@angular/material/dialog';
import { DetalleClienteComponent } from './detalle-cliente/detalle-cliente.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import Swal from 'sweetalert2';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { DialogLoadingComponent } from '../dialog-loading/dialog-loading.component';
import { ArchivosService } from 'app/services/archivos.service';
import { DataService } from 'app/services/data.service';
import { UploadFileComponent } from 'app/shared/upload-file/upload-file.component';

@Component({
  selector: 'app-clientes',
  templateUrl: './clientes.component.html',
  styleUrls: ['./clientes.component.css'],
})
export class ClientesComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  // Configuración de datos Bootstrap compatible
  dataSource = new MatTableDataSource<any>([]);
  paginaSiguiente = 1;
  totalArticulos = 10;
  itemXPag = 20; // OBLIGATORIO: 20 registros por defecto
  currentPage = 0; // Para paginador Angular Material
  search = '';
  lastKeypress: any;
  loading: boolean;
  clientes: Client[] = [];
  
  // Configuración de ordenamiento Bootstrap
  currentSort = {
    column: '',
    direction: 'asc'
  };

  constructor(
    private clienteService: ClientesService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    public router: Router,
    public dataService: DataService,
    private archivoServicio: ArchivosService
  ) {}

  ngOnInit(): void {
    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = 'Clientes y Proveedores';
    }
    this.getClientes();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  /**
   * Ordenamiento Bootstrap compatible
   */
  sortByColumn(column: string) {
    if (this.currentSort.column === column) {
      this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSort.column = column;
      this.currentSort.direction = 'asc';
    }
    
    // Aplicar ordenamiento local o hacer nueva consulta al servidor
    this.applySorting();
  }

  /**
   * Aplicar ordenamiento a los datos
   */
  applySorting() {
    const data = [...this.dataSource.data];
    data.sort((a, b) => {
      const aValue = a[this.currentSort.column];
      const bValue = b[this.currentSort.column];
      
      if (aValue < bValue) {
        return this.currentSort.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return this.currentSort.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    this.dataSource.data = data;
  }

  /**
   * Paginación Angular Material compatible
   */
  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.itemXPag = event.pageSize;
    this.paginaSiguiente = event.pageIndex + 1;
    this.getClientes();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.search = filterValue.trim().toLowerCase();
    if (event.timeStamp - this.lastKeypress > 100) {
      this.paginaSiguiente = 1;
      this.currentPage = 0;
      this.getClientes();
      if (this.paginator) {
        this.paginator.firstPage();
      }
    }
    this.lastKeypress = event.timeStamp;
  }

  getClientes() {
    this.clientes = [];
    this.dataSource.data = [];
    this.loading = true;
    this.clienteService
      .getClientes(this.paginaSiguiente, this.itemXPag, this.search, 'cliente/proveedor')
      .subscribe(
        res => {
          this.clientes = res.data;
          this.dataSource = new MatTableDataSource(res.data);
          this.dataSource.paginator = this.paginator;
          this.totalArticulos = res.total;
          this.loading = false;
        },
        err => {
          this.openSnackBar(
            `Ocurrió un error al obtener listado clientes`,
            'Información'
          );
          this.loading = false;
        }
      );
  }

  openDialog(cliente?: Client) {
    const dialogRef = this.dialog.open(DetalleClienteComponent, {
      minWidth: '50%',
      data: { numero: this.clientes.length + 1, cliente },
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
      this.getClientes();
    });
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000,
    });
  }

  page(event: PageEvent) {
    this.onPageChange(event);
  }

  /**
   * Navegar al resumen del cliente cuando se hace clic en una fila
   */
  verResumen(cliente: Client) {
    this.router.navigate(['/clientes/resumen/', cliente.id]);
  }

  /**
   * Navegar al formulario de edición del cliente
   */
  editarCliente(cliente: Client) {
    this.router.navigate(['/clientes/edit/', cliente.id]);
  }

  /**
   * Descargar un excel con los articulos que tiene el sistema
   */
  descargarExcel() {
    const fileName = `clientes_${new Date().getDate()}${
      new Date().getUTCMonth() + 1
    }${new Date().getFullYear()}${new Date().getHours()}${new Date().getMinutes()}${new Date().getSeconds()}.xlsx`;
    this.dataService.isCloseDialog.next(true);
    const dialogRef = this.dialog.open(DialogLoadingComponent, {
      minWidth: '400',
      minHeight: '400',
      disableClose: true,
      data: {
        mensaje: 'Descargando listado de clientes. Espere ...',
      },
    });

    this.archivoServicio.getClientesExcel().subscribe(
      response => {
        this.dataService.isCloseDialog.next(false);
        this.manageExcelFile(response, fileName);
      },
      err => {
        this.dataService.isCloseDialog.next(false);
        console.log(err);
        this.openSnackBar(
          `Ocurrió un error al descargar listado clientes`,
          'Información'
        );
      }
    );

    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
    });
  }

  manageExcelFile(response: any, fileName: string): void {
    const dataType = response.type;
    const binaryData = [];
    binaryData.push(response);

    const filtePath = window.URL.createObjectURL(
      new Blob(binaryData, { type: dataType })
    );
    const downloadLink = document.createElement('a');
    downloadLink.href = filtePath;
    downloadLink.setAttribute('download', fileName);
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  cargarExcel() {
    const dialogRef = this.dialog.open(UploadFileComponent, {
      minWidth: '400',
      minHeight: '400',
      disableClose: true,
      data: {
        mensaje: 'Subir un archivo excel con los clientes y proveedores',
        type: 'clientes',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      console.log(`Dialog result: ${result}`);
      this.getClientes();
    });
  }

  eliminar(cliente: Client) {
    // Validación para prevenir la eliminación de cliente con ID 1
    if (cliente.id === 1) {
      this.openSnackBar(`No se permite eliminar este cliente`, 'Información');
      return;
    }
    
    Swal.fire({
      title: '¿Desea eliminar el cliente?',
      text: `${cliente.companyName}`,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Si, eliminalo!',
      cancelButtonText: 'No',
    }).then(result => {
      if (result.isConfirmed) {
        this.clienteService.eliminarCliente(cliente.id).subscribe(
          data => {
            this.clientes = data;
            this.getClientes();
            this.openSnackBar(`${cliente.type} eliminado correctamente`, 'Información');
          },
          err => {
            console.log(err);
            this.openSnackBar(`Error al eliminar el cliente`, 'Información');
          }
        );
      }
    });
  }
}
